import React, { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../mock";

const categories = ["All", "Shop", "Collision", "Paint", "Repair", "Finish"];

const Gallery = () => {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null); // index into filtered

  const filtered = filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter);

  const openLightbox = (idx) => setLightbox(idx);
  const close = () => setLightbox(null);
  const nextImg = (e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % filtered.length); };
  const prevImg = (e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + filtered.length) % filtered.length); };

  return (
    <div data-testid="gallery-page">
      <section className="bg-zinc-950 py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-red-600/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.22em] uppercase text-red-500 mb-4">
            <span className="w-8 h-[2px] bg-red-500 inline-block" /> Our Work
          </span>
          <h1 className="font-heading text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-5">
            Shop <span className="text-red-500">Gallery</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed">
            A look inside our facility and the craftsmanship that goes into every vehicle we touch.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-12" data-testid="gallery-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => { setFilter(c); setLightbox(null); }}
                data-testid={`gallery-filter-${c.toLowerCase()}`}
                className={`px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all duration-200 ${
                  filter === c ? "bg-red-600 text-white shadow-md" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((g, idx) => (
              <button
                key={g.id}
                onClick={() => openLightbox(idx)}
                className="group relative rounded-xl overflow-hidden aspect-[4/3] text-left focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
                data-testid={`gallery-item-${g.id}`}
              >
                <img src={g.src} alt={g.caption} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-red-400 mb-1">{g.category}</span>
                  <p className="text-white text-sm font-semibold">{g.caption}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] bg-zinc-950/95 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={close}
          data-testid="gallery-lightbox"
        >
          <button onClick={close} aria-label="Close" className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 text-white hover:bg-red-600 flex items-center justify-center transition-colors duration-200" data-testid="lightbox-close">
            <X size={20} />
          </button>
          <button onClick={prevImg} aria-label="Previous image" className="absolute left-4 sm:left-8 w-11 h-11 rounded-full bg-white/10 text-white hover:bg-red-600 flex items-center justify-center transition-colors duration-200">
            <ChevronLeft size={20} />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={filtered[lightbox].src} alt={filtered[lightbox].caption} className="w-full max-h-[75vh] object-contain rounded-lg" />
            <p className="text-center text-zinc-300 text-sm font-semibold mt-4">{filtered[lightbox].caption}</p>
          </div>
          <button onClick={nextImg} aria-label="Next image" className="absolute right-4 sm:right-8 w-11 h-11 rounded-full bg-white/10 text-white hover:bg-red-600 flex items-center justify-center transition-colors duration-200">
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Gallery;
